/**
 * DemoModeToggle — owner-only toggle that flips a client into demo UX mode.
 *
 * 2026-02-26 (Sachin): "Make sachinm25@gmail.com have a fake card + fake
 * subscription so I can see all workouts and the UX." Wired as a one-click
 * button here so the owner can do it for any account in the future.
 *
 * Demo mode is a UX preview, not a billing change:
 *   - `is_demo: true` + `simulated_card_on_file: true` on the user doc
 *     (renders the billing UI's "card on file" as if real, no Stripe call)
 *   - status → `member` (preserves the prior status on `demo_status_was`)
 *   - A fake `subscriptions` row flagged `is_demo: true`, expires 1 year out
 *   - Auto-renew + auto-pause crons skip `is_demo: true` records
 *
 * Toggling OFF is fully reversible — restores the prior status, removes the
 * fake sub, clears the flags. Audited in activity_log. Owner-only.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Wand2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoModeToggle({ userId, onChanged }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/clients/${userId}/demo-mode`,
                    { withCredentials: true },
                );
                if (alive) setState(r.data);
            } catch (e) {
                // 403 = not owner. Hide the whole panel by leaving state null.
                if (alive) setState(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [userId]);

    const toggle = async () => {
        if (!state) return;
        const next = !state.is_demo;
        const msg = next
            ? `Turn ON demo mode for this account?\n\n• Simulated card on file (no real Stripe charge)\n• Fake $120/mo "Demo Member Plan" sub, expires in 1 year\n• Status flips to "member"\n• Reversible at any time — toggling OFF restores the prior status.`
            : `Turn OFF demo mode?\n\n• Demo subscription is removed\n• Card-on-file flag cleared\n• Status restored to "${state?.demo_subscription ? state.demo_subscription.status : "non_member"}" (or the original pre-demo state).\n\nThis does NOT affect real subscriptions or packs.`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${userId}/demo-mode`,
                { enabled: next },
                { withCredentials: true },
            );
            toast.success(next ? "Demo mode enabled" : "Demo mode disabled", {
                description: next
                    ? "Simulated card + fake subscription are live for this account."
                    : "Account restored to its real billing state.",
            });
            // Refetch the panel state so the badge / button label updates.
            const after = await axios.get(
                `${API}/coach/clients/${userId}/demo-mode`,
                { withCredentials: true },
            );
            setState(after.data);
            onChanged?.();
            window.dispatchEvent(new Event("maxsc:lead-meta-refresh"));
        } catch (e) {
            toast.error("Couldn't toggle demo mode", {
                description: e?.response?.data?.detail || "Unknown error",
            });
        } finally {
            setBusy(false);
        }
    };

    // Hide the panel for non-owners (403'd above) or while still loading.
    if (loading || !state) return null;

    const on = !!state.is_demo;

    return (
        <div
            data-testid="demo-mode-toggle-panel"
            className={`p-3.5 border ${on ? "border-amber-700/60 bg-amber-900/15" : "border-zinc-800 hover:border-amber-700/40"} transition-colors`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <Wand2 className={`w-4 h-4 ${on ? "text-amber-300" : "text-zinc-300"}`} />
                        <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white">
                            Demo mode
                        </div>
                        {on && (
                            <span
                                data-testid="demo-mode-on-badge"
                                className="text-[9px] tracking-[0.2em] uppercase border border-amber-600/70 bg-amber-700/30 text-amber-200 px-1.5 py-0.5"
                            >
                                ON
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-1.5 leading-snug">
                        {on
                            ? "Simulated card + fake subscription active. Account reads as a paying member end-to-end (no real Stripe charges). Auto-renew crons skip this account."
                            : "Flip a client into the paying-member UX without a real card or charge. Useful for onboarding staff or walking through the experience with a prospect."}
                    </p>
                    {on && state.demo_subscription && (
                        <div className="text-[10px] text-amber-300/80 mt-1 font-mono">
                            {state.demo_subscription.plan_name} · ${(state.demo_subscription.price_cents / 100).toFixed(0)}/{state.demo_subscription.interval}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={toggle}
                    disabled={busy}
                    data-testid="demo-mode-toggle-btn"
                    className={`text-[10px] tracking-[0.2em] uppercase px-3 py-2 border whitespace-nowrap transition-colors disabled:opacity-50 ${
                        on
                            ? "border-amber-600/70 text-amber-200 hover:bg-amber-700/30"
                            : "border-zinc-700 text-zinc-300 hover:border-amber-600 hover:bg-amber-700/15"
                    }`}
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : on ? <><X className="w-3.5 h-3.5 inline mr-1" /> Turn off</> : "Turn on"}
                </button>
            </div>
        </div>
    );
}
